import { useCallback, useEffect, useRef, useState } from 'react';
import { callApi } from 'src/utils/apiCall';

/**
 * Fetches the referral-files list for a lead.
 *
 * GET /api/v1/practices/{practiceId}/leads/{leadId}/referral-files/
 * → array of { id, original_name, size, category, status, status_label, created_at, download_available }
 *
 * The backend endpoint may not be deployed yet — a 404 is treated as "no
 * referral files feature here" (notFound=true), never as an error state.
 */
export default function useReferralFiles(practiceId, leadId) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    const fetchFiles = useCallback(async () => {
        if (!practiceId || !leadId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/referral-files/`,
                'GET'
            );
            if (!mountedRef.current) return;
            if (res?.status === 404) {
                setNotFound(true);
                setFiles([]);
            } else if (res?.ok || res?.status === 200) {
                setNotFound(false);
                setFiles(Array.isArray(res?.data) ? res.data : []);
            } else {
                setNotFound(false);
                setError(res?.error || 'Failed to load referral files');
                setFiles([]);
            }
        } catch (e) {
            if (mountedRef.current) {
                setError(e?.message || 'Failed to load referral files');
                setFiles([]);
            }
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [practiceId, leadId]);

    useEffect(() => {
        mountedRef.current = true;
        fetchFiles();
        return () => {
            mountedRef.current = false;
        };
    }, [fetchFiles]);

    return {
        files,
        loading,
        error,
        notFound,
        hasFiles: files.length > 0,
        refetch: fetchFiles,
    };
}
